import React from 'react'

import * as r from '../../../react-utils'

import Button from '@mui/material/Button'

import Pad from '../../comps/Pad'

//@ts-ignore
import styles from './SignatureControlled.module.css'

export interface SignatureControlledProps {
  onRequestClose(): void
  onSign(base64: string): void
  /**
   * 'card': same look as the v2 "Adopt your signature" dialog (pad with x and
   * baseline, Clear + primary action). Default keeps the legacy layout used by
   * the /contract route.
   */
  variant?: 'card'
  submitLabel?: string
}

export default React.memo<SignatureControlledProps>(
  function SignatureControlled({
    onRequestClose,
    onSign,
    variant,
    submitLabel = 'Sign',
  }) {
    const signature = React.useRef<HTMLCanvasElement>(
      document.createElement('canvas'),
    )

    const isMounted = r.useIsMounted()

    const draw = React.useCallback((posX: number, posY: number) => {
      const context = signature.current.getContext('2d')
      if (!context) return

      context.beginPath()
      context.moveTo(initialX as number, initialY as number)
      context.lineWidth = 2
      context.strokeStyle = '#000'
      context.lineCap = 'round'
      context.lineJoin = 'round'
      context.lineTo(posX, posY)
      context.stroke()
      initialX = posX
      initialY = posY
    }, [])

    const getRelativePosition = (clientX: number, clientY: number) => {
      const rect = signature.current.getBoundingClientRect()
      const scaleX = signature.current.width / rect.width
      const scaleY = signature.current.height / rect.height

      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top) * scaleY,
      }
    }

    const touchMove = React.useCallback(
      (e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault()
        const touches = e.changedTouches

        if (touches[0]) {
          const { x, y } = getRelativePosition(
            touches[0]?.clientX,
            touches[0]?.clientY,
          )
          draw(x, y)
        }
      },
      [draw],
    )

    const mouseMove = React.useCallback(
      (e: { clientX: number; clientY: number }) => {
        const { x, y } = getRelativePosition(e.clientX, e.clientY)
        draw(x, y)
      },
      [draw],
    )

    const mouseUp = React.useCallback(() => {
      signature.current.removeEventListener('mousemove', mouseMove)
      initialX = undefined
      initialY = undefined
    }, [mouseMove])

    const mouseDown = React.useCallback(
      (e: { clientX: number; clientY: number }) => {
        const { x, y } = getRelativePosition(e.clientX, e.clientY)
        initialX = x
        initialY = y
        draw(x, y)
        signature.current.addEventListener('mousemove', mouseMove)
      },
      [draw, mouseMove],
    )

    const touchStart = React.useCallback(
      (e: React.TouchEvent<HTMLCanvasElement>) => {
        const touches = e.changedTouches

        if (touches[0]) {
          const { x, y } = getRelativePosition(
            touches[0]?.clientX,
            touches[0]?.clientY,
          )
          initialX = x
          initialY = y
          draw(x, y)
          signature.current.addEventListener(
            'touchmove',
            //@ts-expect-error TO-DO
            touchMove,
          )
        }
      },
      [draw, touchMove],
    )

    const touchUp = React.useCallback(() => {
      signature.current.removeEventListener(
        'touchmove',
        //@ts-expect-error TO-DO
        touchMove,
      )
      initialX = undefined
      initialY = undefined
    }, [touchMove])

    const isCanvasEmpty = React.useCallback(() => {
      const canvas = signature.current
      const context = signature.current.getContext('2d')

      if (!context) return

      const pixelBuffer = new Uint32Array(
        context.getImageData(0, 0, canvas.width, canvas.height).data.buffer,
      )

      return !pixelBuffer.some((color) => color !== 0)
    }, [])

    const compressSignature = (): Promise<Blob> => {
      return new Promise((resolve, reject) => {
        const signatureCanvas = signature.current
        signatureCanvas.toBlob(
          (blob) => {
            if (blob === null) {
              reject(blob)
            } else {
              resolve(blob)
            }
          },
          'image/jpg',
          0.1,
        )
      })
    }

    const signatureToBase64 = React.useCallback(async () => {
      if (isCanvasEmpty()) {
        alert('Signature is empty')
        return
      }

      const image = await compressSignature()

      if (!isMounted()) {
        return
      }
      let reader = new FileReader()
      reader.readAsDataURL(image)
      reader.onload = () => {
        if (!isMounted()) {
          return
        }
        const base64String = reader.result

        onSign(base64String as string)

        onRequestClose()
      }
    }, [isCanvasEmpty, isMounted, onRequestClose, onSign])

    const clear = React.useCallback(() => {
      signature.current
        .getContext('2d')!
        .clearRect(0, 0, signature.current.width, signature.current.height)
    }, [])

    if (variant === 'card')
      return (
        <>
          <p className={styles['hint']}>
            Draw your signature in the box below.
          </p>
          <div className={styles['pad']}>
            <canvas
              className={styles['card-canvas']}
              height={320}
              onMouseDown={mouseDown}
              onMouseUp={mouseUp}
              onTouchEnd={touchUp}
              onTouchStart={touchStart}
              ref={signature}
              width={480}
            />
            <span aria-hidden className={styles['x']}>
              x
            </span>
            <span aria-hidden className={styles['baseline']} />
          </div>
          <div className={styles['actions']}>
            <Button onClick={clear} sx={cardSx.secondary} variant="outlined">
              Clear
            </Button>
            <Button
              onClick={signatureToBase64}
              sx={cardSx.primary}
              variant="contained"
            >
              {submitLabel}
            </Button>
          </div>
        </>
      )
    return (
      <>
        <canvas
          className={styles['canvas']}
          height={320}
          onMouseDown={mouseDown}
          onMouseUp={mouseUp}
          onTouchEnd={touchUp}
          onTouchStart={touchStart}
          ref={signature}
          width={480}
        />

        <Pad amt={20} />
        <div style={sx.containerButtons}>
          <Button onClick={signatureToBase64} variant="contained">
            Save
          </Button>

          <Button color="error" onClick={clear} variant="contained">
            Clear
          </Button>
        </div>
      </>
    )
  },
)

let initialX: unknown
let initialY: unknown

const sx = {
  containerButtons: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
} as const

// Same brand blue as the v2 dialog in Signature.tsx.
const cardSx = {
  primary: {
    backgroundColor: '#1976d2',
    fontWeight: 700,
    textTransform: 'none',
  },
  secondary: {
    borderColor: '#1976d2',
    color: '#1976d2',
    textTransform: 'none',
  },
} as const
